import { useEffect, useState } from "react";
import api from "../constant/api";
import { AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import moment from "moment/moment";
import { ShoppingCart, Trash2, CheckCircle, PackagePlus, UserPlus, Plus } from "lucide-react";

export default function GarmentsPurchaseOrder() {
  const [selectedBranch, setSelectedBranch] = useState(
    localStorage.getItem("selectedBranch") || "2"
  );

  useEffect(() => {
    const updateBranch = () => {
      const branch = localStorage.getItem("selectedBranch");
      setSelectedBranch(branch);
    };
    window.addEventListener("branchChanged", updateBranch);
    return () => window.removeEventListener("branchChanged", updateBranch);
  }, []);

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [supplier, setSupplier] = useState({
    supplier_id: "",
    supplier_name: "",
    phone: "",
    address: "",
  });

  const [newSupplier, setNewSupplier] = useState({
    supplier_name: "",
    phone: "",
    address: "",
  });

  const [productForm, setProductForm] = useState({
    product_name: "",
    product_code: "",
    price: "",
    category: "",
    variants: [{ size: "M", color: "Black", sku: "", qty: 10 }]
  });

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchSuppliers();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await api.get("/GarmentsProduct/getPurchaseOrders");
      setOrders(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/GarmentsProduct/getProducts");
      setProducts(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/Product/getSuppliers");
      setSupplierList(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddVariantRow = () => {
    setProductForm({
      ...productForm,
      variants: [...productForm.variants, { size: "L", color: "White", sku: "", qty: 10 }]
    });
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...productForm.variants];
    updatedVariants[index][field] = value;
    setProductForm({ ...productForm, variants: updatedVariants });
  };

  const handleRemoveVariantRow = (index) => {
    const updatedVariants = productForm.variants.filter((_, i) => i !== index);
    setProductForm({ ...productForm, variants: updatedVariants });
  };

  const handleSaveProductWithVariants = async () => {
    if (!productForm.product_name || !productForm.price) {
      toast.error("Please fill product name and price! ❌");
      return;
    }

    try {
      const res = await api.post("/GarmentsProduct/insertProduct", {
        ...productForm,
        branch: selectedBranch
      });

      if (res.data?.success) {
        toast.success("Garment Product & Variants Added Successfully ✅");
        setShowProductModal(false);
        setProductForm({
          product_name: "",
          product_code: "",
          price: "",
          category: "",
          variants: [{ size: "M", color: "Black", sku: "", qty: 10 }]
        });
        fetchProducts();
      }
    } catch (err) {
      toast.error("Failed to add product ❌");
    }
  };

  const addToCart = (product, variant) => {
    const exists = cart.find(
      (x) => x.garment_product_id === product.garment_product_id && x.variant_id === variant.variant_id
    );

    if (exists) {
      toast.info("Variant already added in cart!");
      return;
    }

    setCart([
      ...cart,
      {
        garment_product_id: product.garment_product_id,
        variant_id: variant.variant_id,
        product_name: product.product_name,
        size: variant.size,
        color: variant.color,
        sku: variant.sku,
        qty: 1,
        cost_price: product.price || 0,
      },
    ]);
    setProductSearch("");
  };

  const updateCart = (variant_id, field, value) => {
    setCart((prev) =>
      prev.map((item) =>
        item.variant_id === variant_id
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const removeCart = (variant_id) => {
    setCart((prev) => prev.filter((x) => x.variant_id !== variant_id));
  };

  const totalAmount = cart.reduce(
    (sum, item) => sum + Number(item.qty || 0) * Number(item.cost_price || 0),
    0
  );

  const createPurchase = async () => {
    if (!supplier.supplier_name) {
      toast.error("Please select a supplier! ❌");
      return;
    }
    if (cart.length === 0) {
      toast.error("Cart is empty! ❌");
      return;
    }

    try {
      const res = await api.post("/GarmentsProduct/createPurchase", {
        supplier_id: supplier.supplier_id,
        branch: selectedBranch,
        supplier_name: supplier.supplier_name,
        phone: supplier.phone,
        address: supplier.address,
        items: cart.map((item) => ({
          ...item,
          totalPrice: Number(item.qty) * Number(item.cost_price),
        })),
        total: totalAmount,
      });

      if (res.data?.success) {
        toast.success("Garments Purchase Order Created Successfully ✅");
        setCart([]);
        setSupplier({ supplier_id: "", supplier_name: "", phone: "", address: "" });
        setSupplierSearch("");
        fetchOrders();
      }
    } catch (err) {
      toast.error("Failed to create purchase order ❌");
    }
  };

  const openOrder = async (purchase_id) => {
    try {
      const res = await api.get(`/GarmentsProduct/getPurchase/${purchase_id}`);
      const orderData = res.data?.order || res.data?.data?.order || res.data;
      const itemsData = res.data?.items || res.data?.data?.items || [];

      if (orderData) {
        setSelectedOrder({
          order: orderData,
          items: itemsData
        });
        setShowModal(true);
      } else {
        toast.error("Order details not found");
      }
    } catch (err) {
      toast.error("Failed to fetch order details ❌");
    }
  };

  // 🔥 REQUIRED FUNCTIONS ADDED TO FIX ESLINT ERRORS
  const updatePurchaseItem = (purchase_item_id, field, value) => {
    setSelectedOrder((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.purchase_item_id === purchase_item_id
          ? {
              ...item,
              [field]: value,
              total:
                Number(field === "qty" ? value : item.qty) *
                Number(field === "cost_price" ? value : item.cost_price),
            }
          : item
      ),
    }));
  };

  const savePurchaseItem = async (item) => {
    try {
      const res = await api.post("/GarmentsProduct/updatePurchaseItem", {
        purchase_item_id: item.purchase_item_id,
        qty: item.qty,
        cost_price: item.cost_price,
      });

      if (res.data?.success) {
        toast.success("Item Updated Successfully ✅");
      }
    } catch (err) {
      toast.error("Failed to update item");
    }
  };

  const addStock = async (purchase_item_id) => {
    if (!window.confirm("Add stock to inventory variants?")) return;

    try {
      const res = await api.post(`/GarmentsProduct/addPurchaseStock/${purchase_item_id}`);
      if (res.data?.success) {
        toast.success("Stock Added to Inventory ✅");
        openOrder(selectedOrder.order.purchase_id);
        fetchOrders();
      }
    } catch (err) {
      toast.error("Failed to add stock");
    }
  };

  const addSupplier = async () => {
    try {
      await api.post("/supplier/createSupplier", newSupplier);
      toast.success("Supplier Added ✅");
      fetchSuppliers();
      setShowSupplierModal(false);
      setNewSupplier({ supplier_name: "", phone: "", address: "" });
    } catch (err) {
      toast.error("Failed to add supplier");
    }
  };

  const filteredSuppliers = supplierList.filter((s) =>
    s.supplier_name?.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const filteredProducts = products.filter((p) =>
    p.product_name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.product_code?.includes(productSearch)
  );

  return (
    <div className="p-4 space-y-6 bg-slate-100 min-h-screen">
      {/* HEADER */}
      <div className="bg-indigo-600 text-white p-4 rounded-2xl shadow-sm flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Garments Purchase Orders</h1>
          <p className="text-xs opacity-80 mt-1">Manage Garment inventory purchases and variant stock</p>
        </div>
        <button
          onClick={() => setShowProductModal(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow flex items-center gap-1.5"
        >
          <Plus size={15} /> + Add New Product & Variants
        </button>
      </div>

      {/* SUPPLIER SECTION */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
        <h2 className="text-xs font-bold uppercase text-slate-700">1. Select Supplier</h2>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search Supplier Name..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              className="w-full border rounded-xl p-2.5 text-xs font-semibold outline-none focus:border-indigo-500"
            />
            {supplierSearch && (
              <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto z-50 mt-1 divide-y">
                {filteredSuppliers.map((s) => (
                  <div
                    key={s.supplier_id}
                    onClick={() => {
                      setSupplier({
                        supplier_id: s.supplier_id,
                        supplier_name: s.supplier_name,
                        phone: s.phone,
                        address: s.address,
                      });
                      setSupplierSearch(s.supplier_name);
                    }}
                    className="p-2.5 hover:bg-slate-50 cursor-pointer text-xs"
                  >
                    <p className="font-bold text-slate-800">{s.supplier_name}</p>
                    <p className="text-[11px] text-slate-500">{s.phone}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => setShowSupplierModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5"
          >
            <UserPlus size={15} /> + Add Supplier
          </button>
        </div>

        {supplier.supplier_name && (
          <div className="border border-indigo-100 bg-indigo-50/50 rounded-xl p-3 text-xs space-y-1">
            <p className="font-bold text-indigo-900">{supplier.supplier_name}</p>
            <p className="text-slate-600">Phone: {supplier.phone}</p>
            <p className="text-slate-500">Address: {supplier.address}</p>
          </div>
        )}
      </div>

      {/* PRODUCT & CART SECTION */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* PRODUCT & VARIANTS SEARCH */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
          <h2 className="text-xs font-bold uppercase text-slate-700">2. Garment Products & Variants</h2>
          <div className="relative">
            <input
              type="text"
              placeholder="Search Garment Product / Code..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="w-full border rounded-xl p-2.5 text-xs font-semibold outline-none focus:border-indigo-500"
            />
            {productSearch && (
              <div className="absolute top-full left-0 right-0 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto z-50 mt-1 p-2 space-y-2">
                {filteredProducts.map((p) => (
                  <div key={p.garment_product_id} className="p-2 border-b">
                    <p className="font-bold text-xs text-slate-800">{p.product_name} - ₹{p.price}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {p.variants?.map((v) => (
                        <button
                          key={v.variant_id}
                          onClick={() => addToCart(p, v)}
                          className="bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white px-2 py-1 rounded-lg text-[11px] font-bold transition"
                        >
                          {v.size} / {v.color} (Stock: {v.qty})
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* PURCHASE CART */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 flex flex-col justify-between space-y-3">
          <div className="flex items-center justify-between border-b pb-2">
            <h2 className="text-xs font-bold uppercase text-slate-700 flex items-center gap-1.5">
              <ShoppingCart size={15} /> Purchase Cart ({cart.length})
            </h2>
          </div>

          <div className="space-y-2.5 max-h-60 overflow-y-auto">
            {cart.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-6">No variants added to cart.</p>
            ) : (
              cart.map((item) => (
                <div key={item.variant_id} className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50/50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-xs text-slate-900">{item.product_name}</p>
                      <p className="text-[11px] text-slate-500">Variant: {item.size} / {item.color}</p>
                    </div>
                    <button onClick={() => removeCart(item.variant_id)} className="text-rose-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <label className="text-[10px] text-slate-500">Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) => updateCart(item.variant_id, "qty", e.target.value)}
                        className="w-full border rounded-lg p-1 text-xs font-bold bg-white outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Cost Price (₹)</label>
                      <input
                        type="number"
                        min="0"
                        value={item.cost_price}
                        onChange={(e) => updateCart(item.variant_id, "cost_price", e.target.value)}
                        className="w-full border rounded-lg p-1 text-xs font-bold bg-white outline-none"
                      />
                    </div>
                  </div>
                  <div className="text-right text-xs font-extrabold text-emerald-600">
                    Total: ₹{Number(item.qty || 0) * Number(item.cost_price || 0)}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t pt-3 flex justify-between items-center">
            <div>
              <p className="text-[11px] text-slate-500">Total Amount</p>
              <h2 className="text-base font-extrabold text-indigo-600">₹{totalAmount.toFixed(2)}</h2>
            </div>
            <button
              onClick={createPurchase}
              disabled={cart.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl text-xs font-bold shadow disabled:opacity-50"
            >
              Create Purchase ✅
            </button>
          </div>
        </div>
      </div>

      {/* PURCHASE ORDERS HISTORY */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
        <h2 className="text-xs font-bold uppercase text-slate-700">Purchase Orders History</h2>
        <div className="grid md:grid-cols-4 gap-3">
          {orders.map((item) => (
            <div key={item.purchase_id} className="border rounded-2xl p-3 shadow-sm bg-slate-50 space-y-2">
              <div className="flex justify-between items-center">
                <span className="font-bold text-xs text-indigo-900">#{item.purchase_id}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${item.status === "Received" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {item.status}
                </span>
              </div>
              <p className="font-semibold text-xs text-slate-800">{item.supplier_name}</p>
              <p className="text-[11px] text-slate-500">Date: {moment(item.created_at).format("DD/MM/YYYY")}</p>
              <p className="text-xs font-extrabold text-slate-900">₹{item.total_amount}</p>
              <button
                onClick={() => openOrder(item.purchase_id)}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-1.5 rounded-xl text-xs font-bold"
              >
                View Items
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* VIEW ORDER ITEMS MODAL */}
      <AnimatePresence>
        {showModal && selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
            <div className="w-full max-w-2xl rounded-3xl bg-white p-5 shadow-2xl space-y-4 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-sm font-bold text-slate-900">
                  Purchase Order #{selectedOrder.order?.purchase_id} - {selectedOrder.order?.supplier_name}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
              </div>

              <div className="space-y-3">
                {selectedOrder.items?.map((item) => (
                  <div key={item.purchase_item_id} className="p-3 rounded-2xl border border-slate-200 bg-slate-50 space-y-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-xs text-slate-900">
                          {item.product_name} ({item.size || 'Free Size'} / {item.color || 'N/A'})
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Qty: {item.qty} | Price: ₹{item.cost_price} | Total: ₹{item.total || (item.qty * item.cost_price)}
                        </p>
                      </div>

                      {item.received === 1 ? (
                        <span className="text-emerald-600 text-xs font-bold flex items-center gap-1">
                          <CheckCircle size={14} /> Received
                        </span>
                      ) : (
                        <button
                          onClick={() => addStock(item.purchase_item_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-xl text-xs font-bold shadow flex items-center gap-1"
                        >
                          <PackagePlus size={13} /> Add Stock
                        </button>
                      )}
                    </div>

                    {item.received !== 1 && (
                      <div className="grid grid-cols-3 gap-2 items-end pt-2 border-t border-slate-200">
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Qty</label>
                          <input
                            type="number"
                            value={item.qty}
                            onChange={(e) => updatePurchaseItem(item.purchase_item_id, "qty", e.target.value)}
                            className="w-full border rounded-lg p-1 text-xs font-bold bg-white outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-500">Cost Price (₹)</label>
                          <input
                            type="number"
                            value={item.cost_price}
                            onChange={(e) => updatePurchaseItem(item.purchase_item_id, "cost_price", e.target.value)}
                            className="w-full border rounded-lg p-1 text-xs font-bold bg-white outline-none"
                          />
                        </div>
                        <button
                          onClick={() => savePurchaseItem(item)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white py-1 rounded-lg text-xs font-bold"
                        >
                          Save Changes
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* ADD PRODUCT & VARIANTS MODAL */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl bg-white p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-sm font-bold text-slate-900">Add New Garment Product & Variants</h3>
              <button onClick={() => setShowProductModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="font-semibold text-slate-600">Product Name</label>
                <input
                  type="text"
                  placeholder="e.g. Cotton Shirt"
                  value={productForm.product_name}
                  onChange={(e) => setProductForm({ ...productForm, product_name: e.target.value })}
                  className="w-full border rounded-xl p-2.5 mt-1 outline-none font-semibold"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-600">Product Code / SKU</label>
                  <input
                    type="text"
                    placeholder="e.g. SHIRT-01"
                    value={productForm.product_code}
                    onChange={(e) => setProductForm({ ...productForm, product_code: e.target.value })}
                    className="w-full border rounded-xl p-2.5 mt-1 outline-none font-semibold"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-600">Price (₹)</label>
                  <input
                    type="number"
                    placeholder="500"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className="w-full border rounded-xl p-2.5 mt-1 outline-none font-semibold"
                  />
                </div>
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between items-center">
                  <label className="font-bold text-slate-800">Variants (Size, Color, Stock)</label>
                  <button
                    type="button"
                    onClick={handleAddVariantRow}
                    className="bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-lg text-[11px] font-bold"
                  >
                    + Add Variant
                  </button>
                </div>

                {productForm.variants.map((v, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2 items-center bg-slate-50 p-2.5 rounded-xl border">
                    <div>
                      <label className="text-[10px] text-slate-500">Size</label>
                      <input
                        type="text"
                        value={v.size}
                        onChange={(e) => handleVariantChange(index, "size", e.target.value)}
                        className="w-full border rounded-lg p-1 bg-white font-bold"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Color</label>
                      <input
                        type="text"
                        value={v.color}
                        onChange={(e) => handleVariantChange(index, "color", e.target.value)}
                        className="w-full border rounded-lg p-1 bg-white font-bold"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500">Initial Qty</label>
                      <input
                        type="number"
                        value={v.qty}
                        onChange={(e) => handleVariantChange(index, "qty", e.target.value)}
                        className="w-full border rounded-lg p-1 bg-white font-bold"
                      />
                    </div>
                    <div className="text-right pt-4">
                      {productForm.variants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveVariantRow(index)}
                          className="text-rose-600 font-bold"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <button onClick={() => setShowProductModal(false)} className="px-4 py-2 text-xs font-bold text-slate-500">Cancel</button>
              <button onClick={handleSaveProductWithVariants} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold">Save Product ✅</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD SUPPLIER MODAL */}
      {showSupplierModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl space-y-3">
            <h3 className="text-sm font-bold text-slate-900 border-b pb-2">Add New Supplier</h3>
            <input
              placeholder="Supplier Name"
              value={newSupplier.supplier_name}
              onChange={(e) => setNewSupplier({ ...newSupplier, supplier_name: e.target.value })}
              className="w-full border rounded-xl p-2.5 text-xs outline-none"
            />
            <input
              placeholder="Phone"
              value={newSupplier.phone}
              onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              className="w-full border rounded-xl p-2.5 text-xs outline-none"
            />
            <textarea
              placeholder="Address"
              value={newSupplier.address}
              onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
              className="w-full border rounded-xl p-2.5 text-xs outline-none"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowSupplierModal(false)} className="px-4 py-2 text-xs font-bold text-slate-500">Cancel</button>
              <button onClick={addSupplier} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold">Save Supplier</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}